'use client';
import { faCartPlus, faClose } from '@fortawesome/pro-regular-svg-icons';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { sendGTMEvent } from '@next/third-parties/google';
import { FC } from 'react';
import { Button, Icon, Image, Link } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import { type DiscountType, useGetDiscountType } from '@/hooks/use-has-discount';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import {
  FavoritesActionButtonsEnum,
  MyCartActionButtonsEnum,
} from '@/types/action-buttons/action-buttons';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import { cn } from '@/utils';
import { selectMainImage } from '@/utils/select-main-image';
import FormattedPrice from '../formatted-price/formatted-price';

type Props =
  | {
      type: 'favorites';
      product: SimpleProductInfo;
    }
  | {
      type: 'cart';
      product: WithQuantity<SimpleProductInfo>;
    };

const TooltipProductCard: FC<Props> = ({ type, product }) => {
  const { t } = useTranslation();
  const {
    addProductToMyCart,
    removeProductFromMyCart,
    isPendingUpdateMyCartProductId,
    actionButtonType,
  } = useMyCart();
  const { addProductToFavorites, removeProductFromFavorites, isFavoritePending } = useFavorites();
  const { features, currency } = useCoreConfig();
  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;
  const shouldShowPrice = (!isAuthenticated && features.priceForGuests) || isAuthenticated;
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;
  const { getDiscountType } = useGetDiscountType();

  // Discount-type resolution differs by auth state in the tooltip card:
  //
  //  * Guests keep the full `useGetDiscountType` flow so the public
  //    catalog can still surface PRODUCT / COMPANY / PRICE_LIST badges.
  //  * Authenticated users always render this as a product-level discount.
  //    The cart / favorites payload that feeds this card already reflects
  //    the user's effective price, and `FormattedPrice` separately resolves
  //    company-category and price-list adjustments at render time, so
  //    branching on those sources here would double-count the discount and
  //    show a misleading strike-through. We just compare the catalog
  //    `price` / `discountedPrice` to decide whether to render the
  //    strike-through "old vs new" pair or a single plain price.
  const discountType: DiscountType = isAuthenticated
    ? product.price > product.discountedPrice
      ? 'PRODUCT'
      : 'NONE'
    : getDiscountType(product, product.parentCategoryId, product.id);

  // The "secondary" action button on a tooltip card moves the product to
  // the OTHER list, not the one the card already belongs to:
  //   * On a cart-popover card (`type === 'cart'`)      → add to favorites
  //   * On a favorites-popover card (`type === 'favorites'`) → add to cart
  // The button label/icon below already follow this rule
  // (`type === 'favorites' ? faCartPlus : faHeart` and
  // `type === 'favorites' ? addToCart : addToFavorites`), so the handler
  // must dispatch the matching mutation. Previously the case bodies were
  // swapped — clicking "Add to cart" on a favorites card silently re-added
  // it to favorites, and vice-versa.
  const onAddProductTo = () => {
    switch (type) {
      case 'cart': {
        const currentEventId = crypto.randomUUID();
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
          event: 'add_to_favorites',
          event_id: currentEventId,
          ecommerce: {
            currency,
            value: product.discountedPrice,
            items: [
              {
                item_id: product.id,
                item_name: product.name,
                price: product.discountedPrice,
                quantity: 1,
                item_category: product.parentCategoryId || '',
              },
            ],
          },
        });
        return addProductToFavorites(
          product,
          { name: 'AddToWishlist', id: currentEventId, sourceUrl: window.location.href },
          FavoritesActionButtonsEnum.TOOLTIP_ADD,
        );
      }
      case 'favorites': {
        const currentEventId = crypto.randomUUID();
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
          event: 'add_to_cart',
          event_id: currentEventId,
          ecommerce: {
            currency,
            value: product.discountedPrice,
            items: [
              {
                item_id: product.id,
                item_name: product.name,
                price: product.discountedPrice,
                quantity: 1,
                item_category: product.parentCategoryId || '',
              },
            ],
          },
        });
        return addProductToMyCart(
          { ...product, quantity: 1 },
          { name: 'AddToCart', id: currentEventId, sourceUrl: window.location.href },
          MyCartActionButtonsEnum.TOOLTIP_ADD,
        );
      }
    }
  };
  const onRemoveProductFrom = () => {
    switch (type) {
      case 'cart': {
        const currentEventId = crypto.randomUUID();
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
          event: 'remove_from_cart',
          event_id: currentEventId,
          ecommerce: {
            currency,
            value: product.discountedPrice * product.quantity,
            items: [
              {
                item_id: product.id,
                item_name: product.name,
                price: product.discountedPrice,
                quantity: product.quantity,
                item_category: product.parentCategoryId || '',
              },
            ],
          },
        });
        return removeProductFromMyCart(
          product,
          { name: 'RemoveFromCart', id: currentEventId, sourceUrl: window.location.href },
          MyCartActionButtonsEnum.TOOLTIP_DELETE,
        );
      }
      case 'favorites': {
        const currentEventId = crypto.randomUUID();
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
          event: 'remove_from_favorites',
          event_id: currentEventId,
          ecommerce: {
            currency,
            value: product.discountedPrice,
            items: [
              {
                item_id: product.id,
                item_name: product.name,
                price: product.discountedPrice,
                item_category: product.parentCategoryId || '',
              },
            ],
          },
        });
        return removeProductFromFavorites(
          product,
          { name: 'RemoveFromWishlist', id: currentEventId, sourceUrl: window.location.href },
          FavoritesActionButtonsEnum.TOOLTIP_DELETE,
        );
      }
    }
  };

  return (
    <Link
      href={`/${product.locale}/product/${product.meta.url}`}
      withoutLocale
      className="group flex w-full shrink-0 cursor-pointer overflow-hidden rounded px-4 py-2 text-black hover:bg-gray-100"
    >
      <div className="flex w-full items-center justify-start gap-4">
        <Image
          width={64}
          height={64}
          src={selectMainImage(product.images)}
          alt={product.name}
          disableSkeleton={false}
          className="h-16 w-16 shrink-0 rounded-md object-cover"
          classNames={{
            wrapper: 'h-16 w-16 shrink-0 rounded-md',
            img: 'h-16 w-16 shrink-0 rounded-md object-cover',
          }}
        />

        <span className="grow text-sm">{product.name}</span>
        {type === 'cart' && (
          <div className="ml-auto flex items-center gap-6">
            <span className="flex items-center gap-1 text-sm font-light text-gray-700">
              <p>x</p>
              <p>{product.quantity}</p>
            </span>
            <div className={cn('flex flex-col items-end', !shouldShowPrice && 'hidden')}>
              {(() => {
                if (discountType === 'PRODUCT') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.quantity * product.discountedPrice}
                        size="md"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                if (discountType === 'PRICE_LIST') {
                  // For price-list discounts the cart-line totals are
                  // resolved inside `FormattedPrice` from the matched
                  // price-list entry (`price` / `discountedPrice`). The
                  // `price` prop here is only a fallback for when the
                  // price-list lookup misses, so multiplying by `quantity`
                  // has no effect on the rendered number.
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.price}
                        size="md"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                if (discountType === 'COMPANY') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.price}
                        size="md"
                        color="red"
                        categoryId={product.parentCategoryId}
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                // NONE
                return (
                  <FormattedPrice
                    price={product.price}
                    size="md"
                    productId={product.id}
                    showVatSuffix
                    isVerticalDirection
                  />
                );
              })()}
            </div>
          </div>
        )}
        {type === 'favorites' && (
          <div className={cn('ml-auto flex items-center gap-6', !shouldShowPrice && 'hidden')}>
            <div className="flex flex-col items-end">
              {(() => {
                if (discountType === 'PRODUCT') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.discountedPrice}
                        size="md"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                if (discountType === 'PRICE_LIST') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.price}
                        size="md"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                if (discountType === 'COMPANY') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="sm"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.price}
                        size="md"
                        color="red"
                        categoryId={product.parentCategoryId}
                        productId={product.id}
                        showVatSuffix
                        isVerticalDirection
                      />
                    </>
                  );
                }

                // NONE
                return (
                  <FormattedPrice
                    price={product.price}
                    size="md"
                    productId={product.id}
                    showVatSuffix
                    isVerticalDirection
                  />
                );
              })()}
            </div>
          </div>
        )}
      </div>
      <div className="ml-auto flex w-fit items-center justify-start gap-4 opacity-0 transition-opacity group-hover:opacity-100">
        <Button
          size="sm"
          variant="flat"
          startContent={
            <Icon
              className={cn({
                '!hidden':
                  type === 'favorites' &&
                  isFavoritePending(product.id, FavoritesActionButtonsEnum.TOOLTIP_DELETE),
              })}
              icon={faClose}
            />
          }
          isLoading={
            type === 'favorites' &&
            isFavoritePending(product.id, FavoritesActionButtonsEnum.TOOLTIP_DELETE)
          }
          onClick={e => {
            e.preventDefault();
            onRemoveProductFrom();
          }}
        >
          {t?.common.remove}
        </Button>
        {((type === 'cart' && features.favorites) || (type === 'favorites' && shouldAllowCart)) && (
          <Button
            size="sm"
            variant="flat"
            color="primary"
            startContent={
              <Icon
                className={cn({
                  '!hidden':
                    (isPendingUpdateMyCartProductId === product.id &&
                      actionButtonType === MyCartActionButtonsEnum.TOOLTIP_ADD) ||
                    isFavoritePending(product.id, FavoritesActionButtonsEnum.TOOLTIP_ADD),
                })}
                icon={type === 'favorites' ? faCartPlus : faHeart}
              />
            }
            isLoading={
              (isPendingUpdateMyCartProductId === product.id &&
                actionButtonType === MyCartActionButtonsEnum.TOOLTIP_ADD) ||
              isFavoritePending(product.id, FavoritesActionButtonsEnum.TOOLTIP_ADD)
            }
            onClick={e => {
              e.preventDefault();
              onAddProductTo();
            }}
          >
            {type === 'favorites' ? t?.common.addToCart : t?.common.addToFavorites}
          </Button>
        )}
      </div>
    </Link>
  );
};

export default TooltipProductCard;
